import { useState } from "react";
import { Button, Empty, Input, Modal, Tooltip, Upload } from "antd";
import type { UploadFile } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatFileSize } from "./medicalReview";
import type {
  MonitoringPlan,
  MonitoringPlanUpload,
} from "./medicalReview.types";

type Props = {
  plan: MonitoringPlan | null;
  canEdit: boolean;
  versionCount: number;
  uploading?: boolean;
  downloading?: boolean;
  onUpload: (draft: MonitoringPlanUpload) => void;
  onDownload: () => void;
  onOpenHistory: () => void;
};

export default function MonitoringPlanView({
  plan,
  canEdit,
  versionCount,
  uploading = false,
  downloading = false,
  onUpload,
  onDownload,
  onOpenHistory,
}: Props) {
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 text-base font-semibold text-[#2B4752]">
            {plan?.title || "Medical Monitoring Plan"}
          </h2>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#556C75]">
            {plan ? (
              <>
                <span>Version {plan.version}</span>
                {plan.plan_reference && <span>· {plan.plan_reference}</span>}
                {plan.authored_by && <span>· {plan.authored_by}</span>}
                {plan.effective_from && (
                  <span>
                    · in force from{" "}
                    {new Date(plan.effective_from).toLocaleDateString()}
                  </span>
                )}
              </>
            ) : (
              <span>No plan has been uploaded for this trial.</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {versionCount > 1 && (
            <Button size="small" type="text" onClick={onOpenHistory}>
              History ({versionCount})
            </Button>
          )}
          {canEdit && (
            <Button size="small" onClick={() => setUploadOpen(true)}>
              {plan ? "Upload new version" : "Upload the plan"}
            </Button>
          )}
        </div>
      </header>

      {plan?.body ? (
        <article className="prose prose-sm max-w-[70ch] text-[#2B4752]">
          <Markdown remarkPlugins={[remarkGfm]}>{plan.body}</Markdown>
        </article>
      ) : plan?.has_file ? (
        <PlanDocumentCard
          plan={plan}
          downloading={downloading}
          onDownload={onDownload}
        />
      ) : (
        <Empty
          description={
            canEdit
              ? "Upload the plan reviewers work to."
              : "No plan document yet."
          }
          className="my-6"
        />
      )}

      <PlanUploadModal
        open={uploadOpen}
        plan={plan}
        uploading={uploading}
        onCancel={() => setUploadOpen(false)}
        onUpload={(draft) => {
          onUpload(draft);
          setUploadOpen(false);
        }}
      />
    </div>
  );
}

/**
 * A plan Vivo cannot render — a PDF or Word document — is still the record of
 * what reviewers are held to, so it is named and offered rather than hidden.
 */
function PlanDocumentCard({
  plan,
  downloading,
  onDownload,
}: {
  plan: MonitoringPlan;
  downloading: boolean;
  onDownload: () => void;
}) {
  const size = formatFileSize(plan.file_size_bytes);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E6ECF1] bg-white px-4 py-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-[#2B4752]">
          {plan.original_filename || "Monitoring plan document"}
        </span>
        <span className="text-xs text-[#8FA5A0]">
          {[plan.content_type, size].filter(Boolean).join(" · ")}
        </span>
      </div>
      <Button loading={downloading} onClick={onDownload}>
        Download
      </Button>
    </div>
  );
}

/**
 * The half a machine holds reviewers to. Shown with the document rather than on
 * a settings screen of its own: a plan nothing checks, and a setting nobody can
 * justify, are the two failure modes this pairing exists to prevent.
 */
function PlanUploadModal({
  plan,
  open,
  uploading,
  onUpload,
  onCancel,
}: {
  plan: MonitoringPlan | null;
  open: boolean;
  uploading: boolean;
  onUpload: (draft: MonitoringPlanUpload) => void;
  onCancel: () => void;
}) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [title, setTitle] = useState(plan?.title ?? "");
  const [reference, setReference] = useState(plan?.plan_reference ?? "");
  const [reason, setReason] = useState("");

  const file = fileList[0]?.originFileObj;
  // A first version has nothing to have changed from; every later one must say
  // why, and the API refuses it otherwise.
  const reasonRequired = plan !== null;
  const canUpload = Boolean(file) && (!reasonRequired || reason.trim());

  const reset = () => {
    setFileList([]);
    setReason("");
  };

  return (
    <Modal
      open={open}
      title={plan ? `Upload version ${plan.version + 1}` : "Upload the plan"}
      okText={uploading ? "Uploading" : "Upload"}
      confirmLoading={uploading}
      okButtonProps={{ disabled: !canUpload }}
      onOk={() => {
        if (!file) return;
        onUpload({
          file: file as File,
          title,
          plan_reference: reference,
          reason_for_change: reason,
        });
        reset();
      }}
      onCancel={() => {
        reset();
        onCancel();
      }}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3 pt-2">
        <Upload.Dragger
          fileList={fileList}
          multiple={false}
          maxCount={1}
          beforeUpload={() => false}
          onChange={({ fileList: next }) => setFileList(next.slice(-1))}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Drop the approved plan here, or click to choose it
          </p>
          <p className="ant-upload-hint">
            PDF and Word are stored and linked; markdown and plain text are also
            shown in place.
          </p>
        </Upload.Dragger>
        <Input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Plan title"
        />
        <Input
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          placeholder="Sponsor document reference"
        />
        {reasonRequired && (
          <Tooltip
            title={
              reason.trim()
                ? ""
                : "Replacing a plan already in force needs a reason."
            }
          >
            <Input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Reason for this change (required)"
            />
          </Tooltip>
        )}
      </div>
    </Modal>
  );
}
